import { FormHelperText } from '@mui/material';
import { FormDatepickerField, FormNumberField, FormSelect, FormTextField } from 'components/forms';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import useSubmitNew from 'shared/hooks/useSubmitNew';
import MassActionModalLayout from 'shared/layouts/massActionModalLayout';
import { MassActionItemProps, UseMassActionProps } from 'shared/types';
import { isNotPastDate, isValidDate } from 'shared/utility/dataGrid';
import { MassActionForm } from 'types/backend';
import { InfoBar } from './ChangeByDollarAmount';
import { StartDateValidation } from './ChangeBySetValue';

export type ChangeByPercentActionForm = {
    percentChange: string;
    start?: string;
    reason?: string;
    comments: string;
    priceType?: string;
};
export type ChangeByPercentForm = MassActionForm<ChangeByPercentActionForm>;

type ChangeByPercentProps<T> = MassActionItemProps<ChangeByPercentForm, T, ChangeByPercentActionForm> &
    StartDateValidation &
    InfoBar;

export default function ChangeByPercent<T>({
    defaultValues,
    onSubmit,
    isLoading,
    tableDataContext,
    rowCount,
    columnConfig,
    allRowsSelected,
    validate,
    startDateValidation,
    InfoBar,
    canSetStartDate,
}: ChangeByPercentProps<T>) {
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useForm<ChangeByPercentForm>({
        defaultValues,
    });

    return (
        <MassActionModalLayout
            isLoading={isLoading}
            allRowsSelected={allRowsSelected}
            tableDataContext={tableDataContext}
            columnConfig={columnConfig}
            rowCount={rowCount}
            submitHandler={handleSubmit(onSubmit)}
        >
            <FormNumberField
                name={'actionForm.percentChange'}
                label='Percent Change'
                control={control}
                rules={{
                    required: 'Percent Change is required',
                    validate,
                }}
                fieldError={errors?.['actionForm']?.['percentChange']}
                type='percent'
            />
            <FormDatepickerField
                name={'actionForm.start'}
                control={control}
                label='Start Date'
                rules={{
                    required: 'Start date is required',
                    validate: startDateValidation
                        ? { isValidDate, isNotPastDate, ...startDateValidation }
                        : { isValidDate, isNotPastDate },
                }}
                fieldError={errors?.['actionForm']?.['start']}
                disabled={!canSetStartDate}
            />
            <FormSelect
                name={'actionForm.reason'}
                label='Reason For Change'
                control={control}
                rules={{ required: 'Reason for change is required' }}
                options={[
                    { value: 'Volume Growth', displayText: 'Volume Growth' },
                    { value: 'Competition', displayText: 'Competition' },
                    { value: 'Resin Change', displayText: 'Resin Change' },
                    { value: 'Other', displayText: 'Other' },
                ]}
                fieldError={errors?.['actionForm']?.['reason']}
            />
            <FormTextField
                name={'actionForm.comments'}
                label='Reason Notes'
                control={control}
                fieldError={errors?.['actionForm']?.['comments']}
            />
            <FormHelperText error sx={{ paddingLeft: '15px' }}>
                {errors?.['actionForm']?.['reason']?.message}
            </FormHelperText>
            {InfoBar && <InfoBar startDateFormValue={watch('actionForm.start')} />}
        </MassActionModalLayout>
    );
}

export function useChangeByPercentMassAction<T>({
    controllerName,
    affectedDataContext,
    selected,
    allRowsSelected,
    onSuccess,
}: UseMassActionProps<T>) {
    const { filtersApplied } = useContext(affectedDataContext);

    const changeByPercentMapper = (form: ChangeByPercentForm) => ({
        ...form,
        filters: filtersApplied,
        selected,
        allRowsSelected,
        type: 'Change market price by x%',
    });

    return useSubmitNew({
        name: `${controllerName}/massaction/changebypercent`,
        mapper: changeByPercentMapper,
        context: affectedDataContext,
        onSuccess: onSuccess,
    });
}
